import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import usePayOverTimeLoanStatements from 'features/payOverTime/hooks/usePayOverTimeLoanStatements';
import { useTranslation } from 'react-i18next';
import { FlatList, StyleSheet, View } from 'react-native';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import EmptyStateScreen from 'shared/patterns/vxl/EmptyStateScreen/EmptyStateScreen';
import ErrorScreen from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import useVaroUserFlow from 'shared/utils/useVaroUserFlow/useVaroUserFlow';

const PayOverTimeStatementsScreen = () => {
  const flow = useVaroUserFlow();
  const router = useRouter();
  const { t: translatedText } = useTranslation('myVaro', {
    keyPrefix: 'statementsAndForms.payOverTimeStatements',
  });
  const { statements, errorModel, loading } =
    usePayOverTimeLoanStatements('cache-and-network');
  const shouldShowEmptyState =
    statements.length === 0 && !loading && !errorModel;

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          title: translatedText('title'),
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <FlatList
        data={statements}
        contentContainerStyle={
          statements.length === 0 ? styles.emptyStateContainer : undefined
        }
        renderItem={({ item }) => (
          <ListItem
            key={item.id}
            primaryCopy={item.fileName}
            onPress={() => {
              router.push({
                pathname: `/${flow}/statements-and-forms/pay-over-time-statements/[id]`,
                params: { id: item.id },
              });
            }}
          />
        )}
        ItemSeparatorComponent={() => (
          <View style={styles.hairLineDivider}>
            <HairLineDivider />
          </View>
        )}
        keyExtractor={(item) => item.id}
        ListEmptyComponent={
          shouldShowEmptyState ? (
            <EmptyStateScreen
              headlineText={translatedText('title')}
              bodyText={translatedText('emptyStateBodyText')}
              illustrationKey="BANK_A"
            />
          ) : null
        }
        removeClippedSubviews={false}
      />
      <ScreenTransitionOverlay
        isLoading={loading}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={errorModel ? <ErrorScreen {...errorModel} /> : null}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flexGrow: 1,
  },
  emptyStateContainer: { flexGrow: 1 },
  hairLineDivider: { marginHorizontal: theme.spacing.m },
});

export default PayOverTimeStatementsScreen;
